import { useState, useEffect, useCallback } from "react";
import { Dialog } from "@remo-co/ui-core/src/components/Dialog";
import { DialogContent } from "@remo-co/ui-core/src/components/DialogContent";
import { DialogTitle } from "@remo-co/ui-core/src/components/DialogTitle";
import { IconButton } from "@remo-co/ui-core/src/components/IconButton";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Close as CloseIcon } from "@remo-co/ui-core/src/icons/Close";
import { useI18n } from "i18n";
import {
  EventEmailCustomizationType,
  IEmailCustomization,
} from "modules/emailCustomization/types";
import { styles } from "./styles";
import { disableLinks } from "./templates/common";
import { generateInviteEmailHtml } from "./templates/inviteEmail";
import { generateRegistrationConfirmationHtml } from "./templates/registrationEmail";
import {
  generateDaysReminderHtml,
  generateMinsReminderHtml,
} from "./templates/reminderEmails";
import { defaultValues } from "./templates/defaults";

interface EmailPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  activeTab: EventEmailCustomizationType;
  customization?: IEmailCustomization;
  senderEmail?: string;
  senderName?: string;
}

const EmailPreviewDialog = ({
  open,
  onClose,
  activeTab,
  customization,
  senderEmail,
  senderName,
}: EmailPreviewDialogProps): JSX.Element => {
  const { t } = useI18n(["eventForm", "event", "common"]);
  const [emailHtml, setEmailHtml] = useState("");

  const generateEmailPreview = useCallback(() => {
    switch (activeTab) {
      case EventEmailCustomizationType.INVITE:
        setEmailHtml(generateInviteEmailHtml(customization));
        break;
      case EventEmailCustomizationType.REGISTRATION_CONFIRMATION:
        setEmailHtml(generateRegistrationConfirmationHtml(customization));
        break;
      case EventEmailCustomizationType.DAYS_REMINDER:
        setEmailHtml(generateDaysReminderHtml(customization));
        break;
      case EventEmailCustomizationType.MINUTES_REMINDER:
        setEmailHtml(generateMinsReminderHtml(customization));
        break;
      default:
        setEmailHtml(generateInviteEmailHtml(customization));
    }
  }, [activeTab, customization]);

  useEffect(() => {
    if (open) {
      generateEmailPreview();
    }
  }, [open, generateEmailPreview]);

  const getEmailSubject = () => {
    if (customization?.subject) {
      return customization.subject;
    }

    const hostName = senderName || defaultValues.fromName;
    const { eventName } = defaultValues;
    const { userName } = defaultValues;
    const daysBefore = defaultValues.days;

    switch (activeTab) {
      case EventEmailCustomizationType.INVITE:
        return t("eventForm:emails.invite.subject", {
          host_name: hostName,
          event_name: eventName,
        });
      case EventEmailCustomizationType.REGISTRATION_CONFIRMATION:
        return t("eventForm:emails.registration.subject", {
          user_name: userName,
          host_name: hostName,
        });
      case EventEmailCustomizationType.DAYS_REMINDER:
        return t("eventForm:emails.days.reminder.subject", {
          user_name: userName,
          days_before_event: daysBefore,
        });
      case EventEmailCustomizationType.MINUTES_REMINDER:
        return t("eventForm:emails.minutes.reminder.subject", {
          user_name: userName,
        });
      default:
        return t("eventForm:emails.invite.subject", {
          host_name: hostName,
          event_name: eventName,
        });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      sx={styles.dialog}
      data-testid="email-preview-dialog"
    >
      <Box sx={styles.dialogContainer}>
        <DialogTitle sx={styles.titleContainer}>
          <Typography sx={styles.titleText} variant="h3">
            {t("eventForm:email.preview")}
          </Typography>
          <IconButton
            data-testid="email-preview-dialog-close"
            aria-label={t("common:button.close")}
            sx={styles.closeButton}
            onClick={onClose}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={styles.content} dividers>
          <Box sx={styles.headerSection}>
            <Box sx={styles.headerSectionItem}>
              <Typography
                variant="subtitle2"
                sx={styles.headerSectionItemLabel}
              >
                {t("eventForm:customize.emails.sender.address.label")}:
              </Typography>
              <Typography variant="body2">
                {`${senderName} <${senderEmail}>`}
              </Typography>
            </Box>
            <Box sx={styles.headerSectionItem}>
              <Typography
                variant="subtitle2"
                sx={styles.headerSectionItemLabel}
              >
                {t("eventForm:customize.emails.subject.line.label")}:
              </Typography>
              <Typography variant="body2">{getEmailSubject()}</Typography>
            </Box>
          </Box>

          {emailHtml && (
            <iframe
              title="Email Preview"
              srcDoc={disableLinks(emailHtml)}
              style={styles.iframe as React.CSSProperties}
              sandbox="allow-same-origin"
              data-testid="email-preview-iframe"
            />
          )}
        </DialogContent>
      </Box>
    </Dialog>
  );
};

export default EmailPreviewDialog;
